// BL-177 Step 2j (E): the map of Settings. One tree, one URL per level, so Back/forward, deep links and refresh all work.
//
//   /settings                                      Settings          (tiles)
//   /settings/profile                              Profile           (page)
//   /settings/communications                       Communications    (tiles)
//   /settings/communications/notifications         Notifications     (3D modal)
//   /settings/communications/signature             Signature         (3D modal; not for the "user" role, as before)
//   /settings/communications/typing-sending        Typing & sending  (3D modal)
//   /settings/communications/conversation-privacy  Conversation privacy (3D modal)
//   /settings/communications/teams-mirror          Teams mirror      (3D modal; only while Microsoft Teams is on for this person)
//   /settings/security                             Sign-in & security (3D modal)
//   /settings/display                              Display           (3D modal)
//   /settings/my-contacts                          My Contacts       (page; its tile shows only while My Contacts is on, or for admins)
//   /settings/services                             Services          (page; its tile and route need Settings: edit)
//
// A node is a 'tiles' level (a grid of tiles), a 'modal' (a Modal3D over its parent's tiles) or a 'page' (a full view).
// Closing a modal, or the breadcrumb, goes up one level. Admin Center pages (/admin/...) are NOT part of this tree.

export const SETTINGS_BASE = '/settings';

export const SETTINGS_NODES = {
    '': {
        key: '', kind: 'tiles', title: 'Settings', crumb: 'Settings',
        subtitle: 'Pick an area. Each one opens its own settings.',
        children: ['profile', 'communications', 'security', 'display', 'my-contacts', 'services'],
    },
    profile: {
        key: 'profile', parent: '', kind: 'page', title: 'Profile', crumb: 'Profile', icon: '👤',
        subtitle: 'Your account details and workspace preferences.',
        blurb: 'Your account details and workspace preferences',
    },
    communications: {
        key: 'communications', parent: '', kind: 'tiles', title: 'Communications', crumb: 'Communications', icon: '💬',
        subtitle: 'Your own settings for alerts, your sign-off, typing and who sees your conversations.',
        blurb: 'Notifications, signature, typing and conversation privacy',
        children: ['communications/notifications', 'communications/signature', 'communications/typing-sending', 'communications/conversation-privacy', 'communications/teams-mirror'],
    },
    'communications/notifications': {
        key: 'communications/notifications', parent: 'communications', kind: 'modal', size: 'wide', title: 'Notifications', crumb: 'Notifications', icon: '🔔',
        subtitle: 'Choose which company-approved alerts reach you, and on which channels.',
        blurb: 'Which alerts reach you, and where',
    },
    'communications/signature': {
        key: 'communications/signature', parent: 'communications', kind: 'modal', size: 'wide', title: 'Signature', crumb: 'Signature', icon: '✍️',
        subtitle: 'Your sign-off for texts, WhatsApp and emails, shown before the company signature.',
        blurb: 'Your sign-off for texts, WhatsApp and email',
        hiddenForUserRole: true,
    },
    'communications/typing-sending': {
        key: 'communications/typing-sending', parent: 'communications', kind: 'modal', title: 'Typing & sending', crumb: 'Typing & sending', icon: '⌨️',
        subtitle: 'How menus open and what the Enter key does when you write a message.',
        blurb: 'Menu opening and Enter key',
    },
    'communications/conversation-privacy': {
        key: 'communications/conversation-privacy', parent: 'communications', kind: 'modal', title: 'Conversation privacy', crumb: 'Conversation privacy', icon: '🔒',
        subtitle: 'Choose how much colleagues can see of your conversations.',
        blurb: 'What colleagues can see about your conversations',
    },
    // Teams PR A (board 3): a person's own Teams connection and mirror preferences. Gated by public.integration_enabled_for_me.
    'communications/teams-mirror': {
        key: 'communications/teams-mirror', parent: 'communications', kind: 'modal', size: 'wide', title: 'Teams mirror', crumb: 'Teams mirror', icon: '👥',
        subtitle: 'Connect your own Microsoft Teams and choose what comes into IntelAgent.',
        blurb: 'Your Teams chats inside IntelAgent',
        requiresTeams: true,
    },
    security: {
        key: 'security', parent: '', kind: 'modal', title: 'Sign-in & security', crumb: 'Sign-in & security', icon: '🛡️',
        subtitle: 'How you sign in to IntelAgent.',
        blurb: 'How you sign in',
    },
    display: {
        key: 'display', parent: '', kind: 'modal', size: 'wide', title: 'Display & theme', crumb: 'Display', icon: '🎨',
        subtitle: 'Light, dark or automatic, for this browser only.',
        blurb: 'Light, dark or auto for this browser',
        tileTitle: 'Display',
    },
    'my-contacts': {
        key: 'my-contacts', parent: '', kind: 'page', title: 'My Contacts', crumb: 'My Contacts', icon: '📇',
        subtitle: 'Your private address book: sharing, and what happens to it if you leave.',
        blurb: 'Your private address book: sharing, and what happens to it if you leave',
    },
    services: {
        key: 'services', parent: '', kind: 'page', title: 'Services', crumb: 'Services', icon: '🚘',
        subtitle: 'Service types, zones, fares and fixed destinations.',
        blurb: 'Manage service types, zones, fares, and fixed destinations',
    },
};

/** '/settings/communications/' -> 'communications'; '/settings' -> ''; anything outside Settings -> null. */
export function settingsKeyFromPath(pathname) {
    const path = String(pathname || '');
    if (path !== SETTINGS_BASE && !path.startsWith(`${SETTINGS_BASE}/`)) return null;
    return path.slice(SETTINGS_BASE.length).replace(/^\/+|\/+$/g, '');
}

export function settingsPath(key) {
    return key ? `${SETTINGS_BASE}/${key}` : SETTINGS_BASE;
}

/** Whether this person's Settings shows the node as a tile (permission gating stays exactly what it was). */
export function isNodeVisible(node, context = {}) {
    if (!node) return false;
    if (node.key === 'my-contacts') return Boolean(context.myContactsOn || context.canManageMyContacts);
    if (node.key === 'services') return Boolean(context.canManageServices);
    if (node.requiresTeams) return Boolean(context.teamsEnabled);
    if (node.hiddenForUserRole) return !context.isUser;
    return true;
}

/** From the root to the node: [{ key, crumb, path }]. */
export function breadcrumbTrail(key) {
    const trail = [];
    let node = SETTINGS_NODES[key];
    while (node) {
        trail.unshift({ key: node.key, crumb: node.crumb, path: settingsPath(node.key) });
        node = node.parent === undefined ? null : SETTINGS_NODES[node.parent];
    }
    return trail;
}

/** The tiles level a node belongs to: itself when it is a tiles level or a page, otherwise its parent (a modal opens over its parent). */
export function levelKeyFor(key) {
    const node = SETTINGS_NODES[key];
    if (!node) return '';
    return node.kind === 'modal' ? node.parent : node.key;
}

/** 0 for the home tiles, 1 for an area's own tiles or a page, ... */
export function levelDepth(levelKey) {
    return breadcrumbTrail(levelKey).length - 1;
}

/**
 * The old personal-settings URLs. Each one lands on the new home of that setting. (The Admin Center redirects under /settings/* stay
 * where they are in CRMApp.) `search` is kept so a link with a query string still carries it.
 */
export const SETTINGS_REDIRECTS = [
    { from: '/settings/account', to: '/settings/profile' },
    { from: '/my-notifications', to: '/settings/communications/notifications' },
    { from: '/settings/notification-preferences', to: '/settings/communications/notifications' },
    { from: '/settings/my-signature', to: '/settings/communications/signature' },
];
